import { useState, useCallback, useRef } from 'react';
import { compareSameDateDesc } from '../utils/lineSort';

// Cursor-based infinite scroll (#1292).
//
// The loader is called with an opaque cursor (undefined for the first page) and
// returns { lines, nextCursor }. Paging follows the server's nextCursor, so it
// bridges arbitrary date gaps - there is no month-walk and no empty-month
// heuristic (which used to strand far-future-dated lines, #829). Does NOT
// auto-load on mount; the consumer calls loadInitialData().
//
// Auto-load cap (#1790): after `autoLoadPageCap` pages (the first page
// included) the scroll sentinel goes quiet and `autoLoadPaused` turns true. The
// consumer then offers a "Load older" control that calls loadMore() per tap, so
// the mounted row count stops growing on its own with scroll depth. Rows are
// never dropped: running balances derive from the newest row downward.
// 2 pages (about 1000 lines) after the owner's dev check on #1790 (#1798).
export const AUTO_LOAD_PAGE_CAP = 2;

export const useInfiniteScroll = (loadDataFunction, { autoLoadPageCap = AUTO_LOAD_PAGE_CAP } = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [pagesLoaded, setPagesLoaded] = useState(0);
  const [error, setError] = useState(null);
  const autoLoadPaused = hasMore && pagesLoaded >= autoLoadPageCap;

  const observerRef = useRef();
  const loadingRef = useRef(false);
  const cursorRef = useRef(null);

  // Sort key for a line (v2 shape): dateStamp, falling back to the lineId prefix
  // `YYYY-MM-DD_*`. Server already orders desc; this is a defensive re-sort so a
  // merged page can never render out of order.
  const getDateForSorting = useCallback((line) => {
    if (line.dateStamp) {
      return new Date(line.dateStamp + 'T00:00:00');
    } else if (line.lineId && typeof line.lineId === 'string') {
      return new Date(line.lineId.substring(0, 10) + 'T00:00:00');
    }
    return new Date(0);
  }, []);

  const sortDesc = useCallback((rows) => (
    rows.slice().sort((a, b) => {
      const dateDiff = getDateForSorting(b).getTime() - getDateForSorting(a).getTime();
      if (dateDiff !== 0) return dateDiff;
      // Same date: payday rank then lineId (#1347), so a TRUE_UP/PAYDAY_FUNDING
      // pair renders in chronological order instead of server key order.
      return compareSameDateDesc(a, b);
    })
  ), [getDateForSorting]);

  // Fetch the first page, replacing any existing data. Returns the page's lines
  // so the caller can react to an empty result (e.g. an empty-state flag).
  const loadInitialData = useCallback(async () => {
    if (loadingRef.current) return [];
    try {
      loadingRef.current = true;
      setLoading(true);
      setError(null);
      const { lines = [], nextCursor = null } = (await loadDataFunction(undefined)) || {};
      cursorRef.current = nextCursor;
      const sorted = sortDesc(lines);
      setData(sorted);
      setHasMore(!!nextCursor);
      setPagesLoaded(1);
      return sorted;
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load data. Please try again.');
      return [];
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [loadDataFunction, sortDesc]);

  // Fetch the next page and append it, deduping by lineId.
  const loadMore = useCallback(async () => {
    if (loadingRef.current || !cursorRef.current) return;
    try {
      loadingRef.current = true;
      setLoadingMore(true);
      const { lines = [], nextCursor = null } = (await loadDataFunction(cursorRef.current)) || {};
      cursorRef.current = nextCursor;
      setData((prevData) => {
        const existingIds = new Set(prevData.map((item) => item.lineId));
        const uniqueNewData = lines.filter((item) => !existingIds.has(item.lineId));
        return sortDesc([...prevData, ...uniqueNewData]);
      });
      setHasMore(!!nextCursor);
      setPagesLoaded((n) => n + 1);
    } catch (err) {
      console.error('Failed to load more data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, [loadDataFunction, sortDesc]);

  // Reload from the first page (after a mutation, or an optimistic-update fallback).
  const reset = useCallback(async () => {
    cursorRef.current = null;
    setHasMore(false);
    return loadInitialData();
  }, [loadInitialData]);

  // Intersection observer sentinel for infinite scroll.
  const lastElementRef = useCallback((node) => {
    // Drop the previous observer before any early return: once auto-load pauses
    // (#1790) a stale observer left on an older row would keep paging past the cap.
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (loading || loadingMore || !hasMore || autoLoadPaused) return;

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadMore();
      }
    }, {
      threshold: 0.1,
      rootMargin: '100px'
    });

    if (node) observerRef.current.observe(node);
  }, [loading, loadingMore, hasMore, autoLoadPaused, loadMore]);

  // Update data optimistically (add/edit/delete before the server round-trip).
  // The result is re-sorted so the list stays in date order after a consumer
  // prepends a past-dated line or edits a date (#1845): an out-of-order row
  // split one date into two groups with the same React key, and removing the
  // row (reconcile under the Unreconciled filter) left a ghost card whose next
  // tap resent a stale version. Row identity is kept, so memoized rows hold.
  const updateData = useCallback((updateFunction) => {
    setData((prevData) => sortDesc(updateFunction(prevData)));
  }, [sortDesc]);

  return {
    data,
    loading,
    loadingMore,
    hasMore,
    autoLoadPaused,
    error,
    loadInitialData,
    loadMore,
    reset,
    lastElementRef,
    updateData,
  };
};
